import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface BattleThemeColors {
  teamA: {
    gradient: string;
    primary: string;
    bright: string;
    dark: string;
    text: string;
    textLight: string;
    shadow: string;
    ring: string;
    bg: string;
    bgLight: string;
    border: string;
  };
  teamB: {
    gradient: string;
    primary: string;
    bright: string;
    dark: string;
    text: string;
    textLight: string;
    shadow: string;
    ring: string;
    bg: string;
    bgLight: string;
    border: string;
  };
  grid: {
    lineColor: string;
    lineColorHex: string;
    lineOpacity: string;
    borderMain: string;
    borderLight: string;
    centerLine: string;
  };
}

export const BATTLE_THEMES = {
  greenPurple: {
    teamA: {
      gradient: 'from-green-400 to-emerald-600',
      primary: '#10b981',
      bright: '#34d399',
      dark: '#059669',
      text: 'text-green-400',
      textLight: 'text-green-300/70',
      shadow: 'shadow-green-500/50',
      ring: 'ring-green-400/30',
      bg: 'bg-green-500/10',
      bgLight: 'bg-green-400/5',
      border: 'border-green-400/30',
    },
    teamB: {
      gradient: 'from-purple-400 to-fuchsia-600',
      primary: '#a855f7',
      bright: '#c084fc',
      dark: '#9333ea',
      text: 'text-purple-400',
      textLight: 'text-purple-300/70',
      shadow: 'shadow-purple-500/50',
      ring: 'ring-purple-400/30',
      bg: 'bg-purple-500/10',
      bgLight: 'bg-purple-400/5',
      border: 'border-purple-400/30',
    },
    grid: {
      lineColor: 'cyan',
      lineColorHex: '#06b6d4',
      lineOpacity: '0.1',
      borderMain: 'bg-cyan-500/30',
      borderLight: 'bg-cyan-500/15',
      centerLine: 'bg-cyan-500/20',
    },
  },
  amberPurple: {
    teamA: {
      gradient: 'from-amber-400 to-yellow-500',
      primary: '#f59e0b',
      bright: '#fbbf24',
      dark: '#d97706',
      text: 'text-amber-400',
      textLight: 'text-amber-300/70',
      shadow: 'shadow-amber-500/50',
      ring: 'ring-amber-400/30',
      bg: 'bg-amber-500/10',
      bgLight: 'bg-amber-400/5',
      border: 'border-amber-400/30',
    },
    teamB: {
      gradient: 'from-purple-400 to-fuchsia-600',
      primary: '#a855f7',
      bright: '#c084fc',
      dark: '#9333ea',
      text: 'text-purple-400',
      textLight: 'text-purple-300/70',
      shadow: 'shadow-purple-500/50',
      ring: 'ring-purple-400/30',
      bg: 'bg-purple-500/10',
      bgLight: 'bg-purple-400/5',
      border: 'border-purple-400/30',
    },
    grid: {
      lineColor: '#e5e7eb',
      lineColorHex: '#e5e7eb',
      lineOpacity: '0.1',
      borderMain: 'bg-gray-300/20',
      borderLight: 'bg-gray-400/10',
      centerLine: 'bg-gray-400/15',
    },
  },
  amberMagenta: {
    teamA: {
      gradient: 'from-amber-400 to-yellow-500',
      primary: '#f59e0b',
      bright: '#fbbf24',
      dark: '#d97706',
      text: 'text-amber-400',
      textLight: 'text-amber-300/70',
      shadow: 'shadow-amber-500/50',
      ring: 'ring-amber-400/30',
      bg: 'bg-amber-500/10',
      bgLight: 'bg-amber-400/5',
      border: 'border-amber-400/30',
    },
    teamB: {
      gradient: 'from-pink-500 to-rose-500',
      primary: '#ec4899',
      bright: '#f472b6',
      dark: '#db2777',
      text: 'text-pink-400',
      textLight: 'text-pink-300/70',
      shadow: 'shadow-pink-500/50',
      ring: 'ring-pink-400/30',
      bg: 'bg-pink-500/10',
      bgLight: 'bg-pink-400/5',
      border: 'border-pink-400/30',
    },
    grid: {
      lineColor: '#e5e7eb',
      lineColorHex: '#e5e7eb',
      lineOpacity: '0.1',
      borderMain: 'bg-gray-300/20',
      borderLight: 'bg-gray-400/10',
      centerLine: 'bg-gray-400/15',
    },
  },
} as const;

export type BattleThemeName = keyof typeof BATTLE_THEMES;

interface ThemeContextType {
  battleTheme: BattleThemeName;
  setBattleTheme: (theme: BattleThemeName) => void;
  colors: BattleThemeColors;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [battleTheme, setBattleTheme] = useState<BattleThemeName>('amberPurple');

  return (
    <ThemeContext.Provider 
      value={{ 
        battleTheme, 
        setBattleTheme,
        colors: BATTLE_THEMES[battleTheme]
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}